/**
 * What a sentinel run is doing right now, derived from its Rolodex entry.
 *
 * The design flow's SSE stream is a relay, not the source of truth: the training
 * job writes its own status and progress onto the entry, and fim-service polls
 * that entry and forwards what it reads. So a page that lost its stream — a
 * refresh, a second tab, another machine — recovers the run by reading the same
 * field the relay reads. Nothing here re-attaches a stream, and nothing here
 * writes: the job and fim-service are the only writers of `summary.status`.
 */

import type { SentinelMode, SentinelRunStatus } from 'features/sentinels/types'

/** Entry id prefixes fim-service gives the non-sentinel uses of this type. */
const UNLISTED_ID_PREFIXES = [
  'fim-sentinel-exp-',
  'fim-sentinel-preview-',
  'fim-sentinel-dctx-',
  'fim-sentinel-case-',
] as const

const UNLISTED_MODES: ReadonlySet<string> = new Set<SentinelMode>([
  'experiment',
  'preview',
  'design_context',
  'case',
])

/**
 * Whether an entry is a sentinel at all.
 *
 * Experiment Lab saves, scratch job-handoff entries and design-context bundles
 * all carry the `fim-sentinel` type, so every consumer of the list has to
 * exclude them. Mode is the real discriminator; the id prefix is a fallback for
 * an entry written before its mode was set (a context bundle's placeholder is
 * written before the job runs, and a run that fails early never gets a fuller
 * summary), which is precisely the in-flight case this list has to get right.
 */
export const isListableSentinel = (entry: {
  id: string
  mode?: string
}): boolean =>
  !UNLISTED_MODES.has(entry.mode ?? '') &&
  !UNLISTED_ID_PREFIXES.some((prefix) => entry.id.startsWith(prefix))

/**
 * `running` is what the entry claims; `stalled` is the UI's judgement that
 * nobody is coming back for it.
 */
export type SentinelRunState = 'running' | 'stalled' | 'done' | 'error'

/**
 * How long a run is believed before the UI stops calling it running.
 *
 * A killed Cloud Run job (OOM, preemption) leaves the entry `running` forever —
 * the job flips the status itself, so a process that dies mid-fit never gets to.
 * Nothing on the backend reaps those, so elapsed time is the only signal there
 * is, and the frontend cannot do better than a judgement call.
 *
 * 45 minutes is that call. fim-service gives up relaying a run after 30
 * (`_RUN_TIMEOUT_S`), so past that no stream is watching anyway and a run has
 * already outlived every server-side deadline; the extra 15 is headroom so a
 * legitimately slow fit is not libelled. Deliberately NOT based on how long the
 * entry has been silent: the full-window refit reports no progress at all, so
 * silence is normal for minutes at a time near the end of every healthy run.
 */
export const SENTINEL_STALLED_AFTER_MS = 45 * 60 * 1000

const parseTimestamp = (value: string | undefined): number | null => {
  if (!value) return null
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? null : parsed
}

export interface SentinelRunTiming {
  status?: SentinelRunStatus
  /** `summary.requested_at`, written with the placeholder before dispatch. */
  requestedAt?: string
  /** `entry.updated_at`; the fallback for entries predating `requested_at`. */
  updatedAt?: string
}

/** Statuses that mean a job still owns the entry and will write to it again. */
const IN_FLIGHT: ReadonlySet<string> = new Set<SentinelRunStatus>([
  'running',
  'building',
])

/** When a still-in-flight entry becomes stalled, or null if that cannot happen. */
export const stalledTransitionAt = (
  timing: SentinelRunTiming
): number | null => {
  if (!IN_FLIGHT.has(timing.status ?? '')) return null
  const startedAt =
    parseTimestamp(timing.requestedAt) ?? parseTimestamp(timing.updatedAt)
  return startedAt === null ? null : startedAt + SENTINEL_STALLED_AFTER_MS
}

/**
 * A missing status means an entry written before the status field existed, and
 * those are all finished runs — the same reading the list and detail views have
 * always taken.
 */
export const resolveRunState = (
  timing: SentinelRunTiming,
  now: number = Date.now()
): SentinelRunState => {
  if (timing.status === 'error') return 'error'
  if (!IN_FLIGHT.has(timing.status ?? '')) return 'done'
  const transitionAt = stalledTransitionAt(timing)
  if (transitionAt === null) return 'running'
  return now >= transitionAt ? 'stalled' : 'running'
}

/**
 * How often to re-read entries in these states, in milliseconds; 0 is off.
 *
 * Polling substitutes for a stream that cannot be re-attached, so it has to pay
 * for itself. Every visitor to the page pays for the list query, so it only runs
 * while this site genuinely has a job in flight. 15s is well under the time any
 * stage of a run takes and well over the 3s the job throttles its own progress
 * writes to, so nothing is missed by waiting.
 *
 * A stalled run polls at ZERO. Past the threshold the odds of a late finish are
 * low and the cost of watching for one is forever, which is the wrong trade for
 * a query on the critical path of a page. The late finish is still not lost:
 * SWR revalidates on focus, so returning to the tab picks it up.
 */
export const SENTINEL_RUNNING_POLL_MS = 15_000

export const sentinelPollInterval = (
  states: Iterable<SentinelRunState>
): number => {
  for (const state of states) {
    if (state === 'running') return SENTINEL_RUNNING_POLL_MS
  }
  return 0
}

/** The earliest moment any of these entries could turn stalled. */
export const nextStalledTransition = (
  timings: Iterable<SentinelRunTiming>
): number | null => {
  let earliest: number | null = null
  for (const timing of timings) {
    const at = stalledTransitionAt(timing)
    if (at !== null && (earliest === null || at < earliest)) earliest = at
  }
  return earliest
}

/**
 * The context id inside a design-context entry id, or undefined if the id is not
 * one.
 *
 * fim-service builds those ids as `fim-sentinel-dctx-<site>-<context id>` from a
 * context id that is already 16 hex characters, which is also its truncation
 * limit — so nothing is lost and reading it back is exact. The fallback for a
 * run whose `request.json` cannot be fetched, since re-running needs the context
 * id rather than the entry id.
 */
export const contextIdFromEntryId = (
  entryId: string | undefined
): string | undefined => {
  const match = /^fim-sentinel-dctx-[^-]+-([a-z0-9]+)$/.exec(entryId ?? '')
  return match?.[1]
}
